import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Activity, Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
// DTC-K: route the trends-window anchor through the canonical clock
// so analytics queries are deterministic under `clock.setFixed(...)`.
import { clock } from "@/utils/clock";

interface JobPerformanceTrendsProps {
  shopId: string;
  jobId?: string;
}

type TimeRange = "7d" | "30d" | "90d";

export function JobPerformanceTrends({ shopId, jobId }: JobPerformanceTrendsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");

  const { data: trendData, isLoading } = useQuery({
    queryKey: ['job-performance-trends', shopId, jobId, timeRange],
    queryFn: async () => {
      const daysBack = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
      const startDate = clock.now();
      startDate.setDate(startDate.getDate() - daysBack);

      // FIX-R131-MED-2: previously `.order('updated_at', { ascending: true })
      // .limit(20)`. ASC order + limit-20 took the 20 OLDEST stats rows in
      // the window. The client-side `.slice(0, 10)` (line ~63) after a
      // completions-sort tried to pick the actual top performers — but in
      // any shop with >20 active job categories, the 30+ jobs with the
      // most recent updates were excluded entirely, so "Top Jobs by
      // Completion" could miss the real top performers. Order at the DB
      // layer should match the widget intent. Sort by `total_completions`
      // DESC and raise the cap to 100 so the client sort never misses the
      // top 10.
      let query = supabase
        .from('pensieve_job_stats')
        .select(`
          *,
          jobs!inner(name, category)
        `)
        .eq('shop_id', shopId)
        .is('tech_id', null)
        .gte('updated_at', startDate.toISOString())
        .order('total_completions', { ascending: false });

      if (jobId) {
        query = query.eq('job_id', jobId);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;

      // Group by job and aggregate
      const jobMap = new Map<string, any[]>();
      data?.forEach(stat => {
        const key = stat.jobs.name;
        if (!jobMap.has(key)) {
          jobMap.set(key, []);
        }
        jobMap.get(key)!.push(stat);
      });

      // Get latest stats for each job
      return Array.from(jobMap.entries()).map(([jobName, stats]) => {
        const latest = stats[stats.length - 1];
        return {
          job: jobName.length > 20 ? jobName.substring(0, 20) + '...' : jobName,
          avgTime: Math.round(latest.avg_actual_minutes || 0),
          efficiency: Math.round((latest.efficiency_ratio || 0) * 100),
          completions: latest.total_completions || 0
        };
      }).sort((a, b) => b.completions - a.completions || a.job.localeCompare(b.job)).slice(0, 10);
    }
  });

  if (isLoading) {
    return <Card className="p-4"><div className="h-80 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!trendData || trendData.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Job Performance Trends</h3>
        </div>
        <p className="text-sm text-muted-foreground">No job performance data available yet</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Top Jobs by Completion</h3>
        </div>
        <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
          <SelectTrigger className="w-32">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 Days</SelectItem>
            <SelectItem value="30d">30 Days</SelectItem>
            <SelectItem value="90d">90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={trendData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis 
            type="number"
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <YAxis 
            dataKey="job" 
            type="category" 
            width={150}
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.5rem',
              color: 'hsl(var(--foreground))'
            }}
          />
          <Legend 
            wrapperStyle={{
              color: 'hsl(var(--foreground))'
            }}
          />
          <Bar 
            dataKey="completions" 
            fill="hsl(var(--primary))" 
            name="Completions"
          />
          <Bar 
            dataKey="efficiency" 
            fill="hsl(var(--success))" 
            name="Efficiency %"
          />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}
